import { BigNumber } from '@injectivelabs/utils'
import { StopReason } from '@/types'
import type { IntervalOption } from '@/types'

export * from './setup'
export * from './campaign'
export * from './mega-vault'

export const UI_ZERO_DECIMAL = 0
export const UI_DEFAULT_MAX_DECIMALS = 18
export const UI_DEFAULT_MIN_DISPLAY_DECIMALS = 2
export const UI_DEFAULT_DISPLAY_DECIMALS = 4
export const UI_DEFAULT_LOW_PRICE_DISPLAY_DECIMALS = 9
export const UI_DEFAULT_MAX_DISPLAY_DECIMALS = 6
export const UI_DEFAULT_PRICE_DISPLAY_DECIMALS = 4
export const UI_DEFAULT_AMOUNT_DISPLAY_DECIMALS = 4
export const UI_DEFAULT_AGGREGATION_DECIMALS = 3
export const UI_DEFAULT_FUNDING_RATE_DECIMALS = 5
export const UI_DEFAULT_AGGREGATION_DECIMALS_STRING = '3'
export const UI_DEFAULT_BINARY_OPTIONS_PRICE_DECIMALS = 4
export const UI_DEFAULT_TOKEN_ASSET_DECIMALS = 8
export const UI_DEFAULT_MAX_NUMBER_OF_ORDERS = 20

export const UI_DEFAULT_PRICE_MIN_DECIMALS = 10
export const UI_DEFAULT_PRICE_MAX_DECIMALS = 0.00001

export const UI_DEFAULT_LEVERAGE = '1'
export const UI_AMOUNT_ABBREVIATION_FLOOR = 1_000_000

export const UI_ORDERBOOK_AGGREGATED_BUY_KEY = '-1'
export const UI_ORDERBOOK_AGGREGATED_SELL_KEY = '-1'
export const UI_MINIMAL_AMOUNT = toBigNumber(1).shiftedBy(
  -UI_DEFAULT_MIN_DISPLAY_DECIMALS
)
export const MAX_QUOTE_DECIMALS = 3
export const MAX_QUOTE_TENS_MULTIPLIER = 3
export const UI_MAX_PAGINATION_LIMIT_COUNT = 50

export const BIG_NUMBER_ROUND_HALF_UP_MODE = BigNumber.ROUND_HALF_UP
// 6 gwei for Kovan, fetched from gasStation for Mainnet
export const BIGGER_PRICE_WARNING_DEVIATION = toBigNumber(30) // 30%
export const DEFAULT_MARKET_PRICE_WARNING_DEVIATION = toBigNumber(10) // 10%
export const MAX_SLIPPAGE = toBigNumber(50) // +50% slippage
export const MIN_SLIPPAGE = toBigNumber(0.01) // +0.01% slippage
export const DEFAULT_SLIPPAGE = toBigNumber(0.5) // +0.5% slippage
export const HIGH_SLIPPAGE_THRESHOLD = toBigNumber(5) // +5% slippage
export const DEFAULT_MAX_SLIPPAGE_FOR_CLOSING_POSITIONS = toBigNumber(100) // +100% slippage
export const DEFAULT_MIN_PRICE_BAND_DIFFERENCE = toBigNumber(80) // -80%
export const DEFAULT_MAX_PRICE_BAND_DIFFERENCE = toBigNumber(400) // +400%
export const DEFAULT_CAPPED_TRADE_AND_EARN_REWARDS = 25
export const DEFAULT_EST_SLIPPAGE = 0
export const MIN_EST_SLIPPAGE = 0.0005
export const DEFAULT_TP_SL_PERCENTAGE = 0.1

export const MAX_DISPLAYABLE_NUMBER = toBigNumber(1_000_000_000)

export const MIN_AMOUNT_REQUIRED_FOR_GAS_REBATE = 500
export const MIN_TIMESTAMP_REQUIRED_FOR_GAS_REBATE = 1638313200 // 01 Dec 2020 00:00
export const OLP_TIME_STAMP_FORMAT = "MMM-dd-yyyy HH:mm:ss 'UTC'xxx"

export const DATE_TIME_DISPLAY = 'dd MMM HH:mm:ss'

export const VPN_PROXY_VALIDATION_PERIOD = 7 /* 7 days */

export const HIDDEN_BALANCE_DISPLAY = '********'
export const SMALL_BALANCE_THRESHOLD = '10'
export const LOW_VOLUME_MARKET_THRESHOLD = 1000
export const LOW_FEE_AMOUNT_THRESHOLD = 0.0001
export const UI_DEFAULT_FEE_MIN_DECIMALS = 8

export const UI_DEFAULT_PAGINATION_LIMIT_COUNT = 10
export const UI_MINIMAL_ABBREVIATION_FLOOR = 1_000_000

export const MARKETS_HISTORY_CHART_SEVEN_DAYS = 154
export const MARKETS_HISTORY_CHART_ONE_HOUR = 60

export const TRADE_FORM_PRICE_ROUNDING_MODE = BigNumber.ROUND_HALF_UP
export const TRADE_FORM_QUANTITY_ROUNDING_MODE = BigNumber.ROUND_DOWN

export const BINANCE_DEPOSIT_ADDRESSES = [
  'inj1u2rajhqtptzvu23leheta9yg99k3hazf4waf43'
]

export const TRADE_MAX_SUBACCOUNT_ARRAY_SIZE = 100

export const MAX_SYMBOL_LENGTH = 6
export const SYMBOL_DISPLAY_LENGTH = 3

export const DUST_AMOUNT_THRESHOLD = 1
export const DEFAULT_TRUNCATE_LENGTH = 6

export const GST_MINIMUM_GRIDS = 3
export const GST_MAXIMUM_GRIDS = 150
export const GST_MIN_TRADING_SIZE = 20
export const GST_MIN_TRADING_SIZE_LOW = 1
export const GST_GRID_THRESHOLD = 10
export const GST_DEFAULT_AUTO_GRIDS = 20
export const GST_DEFAULT_PRICE_TICK_SIZE = '0.001'
export const GST_SINGLE_SIDED_THRESHOLD = 0.01
export const GST_KAVA_SINGLE_SIDED_THRESHOLD = 0.005
export const GST_STABLE_LOWER_PRICE = '0.999'
export const GST_STABLE_UPPER_PRICE = '1.001'
export const GST_STABLE_LOWER_PERCENTAGE = '0.99'
export const GST_STABLE_UPPER_PERCENTAGE = '1.01'
export const GST_STABLE_GRIDS = 3
export const GST_AUTO_PRICE_THRESHOLD = 2
export const GST_MIN_TOTAL_AMOUNT_USD = 50

export const PGT_MIN_TRADING_SIZE_LOW = 2

export const GST_ROUTE = 'trading-bots-grid-spot'

export const DEFAULT_LP_ROUND = '1'
export const USDT_TOKEN_DECIMALS = 6
export const MAINNET_UPGRADE_BLOCK_HEIGHT = 57076000
export const POST_ONLY_MODE_BLOCK_THRESHOLD = 2000

export const ADMIN_UI_SMART_CONTRACT =
  'inj1xufs3ggc9styawwhfp23q9jz7kxmm7tek8yflk'
export const NEPTUNE_USDT_BUFFER = 0.15

export const ORDERBOOK_ROW_HEIGHT = 24
export const ORDERBOOK_ROWS = 11
export const ORDERBOOK_HEADER_HEIGHT = 56
export const MAXIMUM_LEADERBOARD_STATS_RANK = 100
export const MAXIMUM_RANKED_TRADERS = 500
export const MIN_LEADERBOARD_PNL_AMOUNT = 50
export const MIN_COMPETITION_PNL_AMOUNT = 0.01
export const DEFAULT_MIN_GAS = 1_000_000_000_000_000 // 0.001 INJ

export const intervalOptions: IntervalOption[] = [
  { label: '1m', value: { resolution: 1, countback: 30 * 32 } },
  { label: '5m', value: { resolution: 5, countback: 30 * 32 } },
  { label: '15m', value: { resolution: 15, countback: 30 * 32 } },
  { label: '30m', value: { resolution: 30, countback: 30 * 32 } },
  { label: '1h', value: { resolution: 60, countback: 30 * 32 } },
  { label: '2h', value: { resolution: 120, countback: 30 * 16 } },
  { label: '4h', value: { resolution: 240, countback: 30 * 10 } },
  { label: '12h', value: { resolution: 720, countback: 30 * 10 } },
  { label: '1D', value: { resolution: 1440, countback: 30 * 10 } }
]
export const LEGACY_MARKET_IDS = [
  '0xac938722067b1dfdfbf346d2434573fb26cb090d309b19af17df2c6827ceb32c',
  '0xb9a07515a5c239fcbfa3e25eaa829a03d46c4b52b9ab8ee6be471e9eb0e9ea31',
  '0x1bba49ea1eb64958a19b66c450e241f17151bc2e5ea81ed5e2793af45598b906'
]

export const SGT_STABLE_COINS = ['USDTkv', 'STINJ'].map((symbol) =>
  symbol.toLowerCase()
)

export const CURRENT_MARKET_TO_LEGACY_MARKET_ID_MAP = {
  // SOL - SOLlegacy
  '0xd9089235d2c1b07261cbb2071f4f5a7f92fa1eca940e3cad88bb671c288a972f':
    '0xac938722067b1dfdfbf346d2434573fb26cb090d309b19af17df2c6827ceb32c',
  // WMATIC - WMATIClegacy
  '0x8cd25fdc0d7aad678eb998248f3d1771a2d27c964a7630e6ffa5406de7ea54c1':
    '0xb9a07515a5c239fcbfa3e25eaa829a03d46c4b52b9ab8ee6be471e9eb0e9ea31',
  // ARB - ARBlegacy
  '0x1c2e5b1b4b1269ff893b4817a478fba6095a89a3e5ce0cccfcafa72b3941eeb6':
    '0x1bba49ea1eb64958a19b66c450e241f17151bc2e5ea81ed5e2793af45598b906'
} as Record<string, string>

export const LEGACY_MARKET_TO_CURRENT_MARKET_ID_MAP = Object.entries(
  CURRENT_MARKET_TO_LEGACY_MARKET_ID_MAP
).reduce(
  (acc, [current, legacy]) => {
    acc[legacy] = current

    return acc
  },
  {} as Record<string, string>
)

export const STOP_REASON_MAP = {
  [StopReason.User]: 'tradingBots.user',
  [StopReason.StopLoss]: 'trade.stopLoss',
  [StopReason.TakeProfit]: 'trade.takeProfit',
  [StopReason.InsufficientFunds]: 'tradingBots.insufficientFunds',
  [StopReason.ExceededMaxRetries]: 'tradingBots.exceededMaxRetries',
  [StopReason.Emergency]: 'tradingBots.marketConditionsNotSupported'
}

export const HELIX_MOBILE_CONNECT_SERVER_URL =
  import.meta.env.VITE_HELIX_MOBILE_CONNECT_SERVER_URL || ''

export const LIGHT_CHART_MARKET_IDS = [
  '0xe5bfc48fc29146d756c9dac69f096d56cc4fc5ae75c98c1ad045c3356d14eb82' // AIX/USDT PERP
]

export const INDEX_MARKETS_INFO = [
  {
    label: '$AIX Index',
    link: 'https://docs.helixapp.com/trading/perpetuals/helix-ai-index-aix',
    marketId:
      '0xe5bfc48fc29146d756c9dac69f096d56cc4fc5ae75c98c1ad045c3356d14eb82'
  }
]

export const DEFAULT_100_CHART_CANDLE_BAR_SPACING = 9.2
export const CHART_ZOOM_FALLBACK_NUMBER = 0.5

export const MAX_LIMIT_ORDER_LINES = 3
export const MAX_TOAST_TIMEOUT = 10 * 24 * 60 * 60 * 1000 // 10 days

export const DEPRECATED_WALLET_DOCS_LINK =
  'https://docs.helixapp.com/getting-started/sign-up/google-account-wallet'

export const DEBOUNCE_DEFAULT_PERIOD = 200
